import { useQuery } from "@tanstack/react-query";
import type { PaginationState, SortingState } from "@tanstack/react-table";

import { fetchTableData, getIntakeDetails, getTransferDetails } from "@/api";
import type { PatientsListStatus } from "@/utils";
import { PATIENTS_LIST_STATUS } from "@/utils";

export interface TransferRow {
  id: string;
  patientFullName: string;
  primaryInsuranceName: string;
  agentName: string;
  createdAt: string;
}

export interface PatientRow {
  id: string;
  patientFullName: string;
  primaryInsuranceName: string;
  agentName: string;
  createdAt: string;
}

export interface DisqualifiedRow {
  id: string;
  patientFullName: string;
  reasonToDisqualify: string;
  disqualifiedAt: string;
}

export const usePatientsLists = () => {
  const useTransferListCases = (
    sorting: SortingState,
    pagination: PaginationState,
    fullNameSearchQuery?: string,
  ) => {
    const sortKey = sorting[0]?.id;
    const sortDirection = sorting[0]?.desc ? "desc" : "asc";

    return useQuery({
      queryKey: ["transferCases", sorting, pagination, fullNameSearchQuery],
      queryFn: () =>
        fetchTableData<TransferRow[]>({
          basePath: "/transfers",
          page: pagination.pageIndex,
          pageSize: pagination.pageSize,
          sortKey,
          sortDirection,
          fullNameSearchQuery,
        }),
    });
  };
  const useDisqualifiedListCases = (
    sorting: SortingState,
    pagination: PaginationState,
    fullNameSearchQuery?: string,
  ) => {
    const sortKey = sorting[0]?.id;
    const sortDirection = sorting[0]?.desc ? "desc" : "asc";

    return useQuery({
      queryKey: ["disqualifiedCases", sorting, pagination, fullNameSearchQuery],
      queryFn: () =>
        fetchTableData<DisqualifiedRow[]>({
          basePath: "/disqualified",
          page: pagination.pageIndex,
          pageSize: pagination.pageSize,
          sortKey,
          sortDirection,
          fullNameSearchQuery,
        }),
    });
  };
  const usePatientsListCases = (
    sorting: SortingState,
    pagination: PaginationState,
    fullNameSearchQuery?: string,
  ) => {
    const sortKey = sorting[0]?.id;
    const sortDirection = sorting[0]?.desc ? "desc" : "asc";

    return useQuery({
      queryKey: ["patientsCases", sorting, pagination, fullNameSearchQuery],
      queryFn: () =>
        fetchTableData<PatientRow[]>({
          basePath: "/intakes",
          page: pagination.pageIndex,
          pageSize: pagination.pageSize,
          sortKey,
          sortDirection,
          fullNameSearchQuery,
        }),
    });
  };

  const usePatientDetails = (
    patientId: string,
    patientStatus: PatientsListStatus,
    enabled: boolean,
  ) => {
    return useQuery({
      queryKey: ["patientDetails", patientStatus, patientId],
      queryFn: () => {
        if (patientStatus === PATIENTS_LIST_STATUS.TRANSFER) {
          return getTransferDetails(patientId);
        }
        return getIntakeDetails(patientId);
      },
      enabled,
    });
  };
  return {
    useTransferListCases,
    useDisqualifiedListCases,
    usePatientsListCases,
    usePatientDetails,
  };
};
